import type { MapSlotDef, MineInstance, SlotState } from '../types'

export interface SlotRuntime {
  id: string
  neighborIds: string[]
  hasMine: boolean
  state: SlotState
}

export type RevealFromStepResult =
  | { kind: 'revealed'; count: number }
  | { kind: 'exploded' }
  | { kind: 'noop' }

export type CompleteDefuseResult = 'defused' | 'empty' | 'invalid'

export class MineFieldGraph {
  readonly slots = new Map<string, SlotRuntime>()
  /** Мета мин (offset + channel duration) — только для hasMine */
  readonly mines = new Map<string, MineInstance>()

  constructor(defs: MapSlotDef[]) {
    for (const def of defs) {
      this.slots.set(def.id, {
        id: def.id,
        neighborIds: [...def.neighborIds],
        hasMine: false,
        state: 'unknown',
      })
    }
  }

  get size(): number {
    return this.slots.size
  }

  getSlot(id: string): SlotRuntime | undefined {
    return this.slots.get(id)
  }

  getMine(id: string): MineInstance | undefined {
    return this.mines.get(id)
  }

  ids(): string[] {
    return [...this.slots.keys()]
  }

  resetMines(mineIds: Iterable<string>, instances?: Iterable<MineInstance>): void {
    const set = new Set(mineIds)
    this.mines.clear()
    for (const slot of this.slots.values()) {
      slot.hasMine = set.has(slot.id)
      slot.state = 'unknown'
    }
    if (instances) {
      for (const m of instances) {
        if (set.has(m.slotId)) this.mines.set(m.slotId, m)
      }
    }
  }

  adjacentMineCount(slotId: string): number {
    const slot = this.slots.get(slotId)
    if (!slot) return 0
    let count = 0
    for (const nid of slot.neighborIds) {
      const n = this.slots.get(nid)
      if (n?.hasMine) count += 1
    }
    return count
  }

  /**
   * Step-reveal: только текущий слот.
   * Мина (не defused) → exploded; safe unknown → safeRevealed + count.
   */
  revealFromStep(slotId: string): RevealFromStepResult {
    const slot = this.slots.get(slotId)
    if (!slot) return { kind: 'noop' }

    if (slot.hasMine && slot.state !== 'defused') {
      slot.state = 'mineExploded'
      return { kind: 'exploded' }
    }

    if (!slot.hasMine && slot.state === 'unknown') {
      slot.state = 'safeRevealed'
      return { kind: 'revealed', count: this.adjacentMineCount(slotId) }
    }

    return { kind: 'noop' }
  }

  /**
   * Завершение channel: мина → defused; empty → слот остаётся unknown.
   */
  completeDefuse(slotId: string): CompleteDefuseResult {
    const slot = this.slots.get(slotId)
    if (!slot) return 'invalid'
    if (slot.state !== 'unknown') return 'invalid'

    if (slot.hasMine) {
      slot.state = 'defused'
      return 'defused'
    }
    return 'empty'
  }

  /** Можно ли начать channel на слоте (unknown mine или empty). */
  canBeginDefuse(slotId: string): boolean {
    const slot = this.slots.get(slotId)
    return !!slot && slot.state === 'unknown'
  }

  channelDurationFor(slotId: string, emptyChannelSec: number): number {
    const mine = this.mines.get(slotId)
    if (mine) return mine.channelDurationSec
    return emptyChannelSec
  }

  mineCount(): number {
    let n = 0
    for (const s of this.slots.values()) if (s.hasMine) n += 1
    return n
  }

  defusedCount(): number {
    let n = 0
    for (const s of this.slots.values()) if (s.state === 'defused') n += 1
    return n
  }

  safeTotal(): number {
    let n = 0
    for (const s of this.slots.values()) if (!s.hasMine) n += 1
    return n
  }

  safeRevealedCount(): number {
    let n = 0
    for (const s of this.slots.values()) if (s.state === 'safeRevealed') n += 1
    return n
  }

  allMinesDefused(): boolean {
    for (const s of this.slots.values()) {
      if (s.hasMine && s.state !== 'defused') return false
    }
    return this.mineCount() > 0
  }
}
